import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Droplets, Sparkles, Train } from "lucide-react";
import { useT } from "@/i18n/LocaleContext";
import HammLogoA from "./HammLogoA";

const ICONS = [Droplets, Train, Sparkles];

export default function HygieneSection() {
  const { t, dict, locale } = useT();
  const cards = dict.hygieneSection.cards;

  return (
    <section id="hygiene" className="py-32 lg:py-44 bg-white" data-testid="hygiene-section">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-20 lg:mb-24 max-w-3xl"
        >
          <p className="font-tech text-[11px] tracking-[0.3em] uppercase text-[var(--ci-blue)] mb-6">{t("hygieneSection.eyebrow")}</p>
          <h2 className="text-3xl lg:text-5xl font-bold tracking-tight text-[var(--ci-grey)]">
            {t("hygieneSection.title1")}<span className="text-[var(--ci-blue)]">{t("hygieneSection.title2")}</span>
          </h2>
          <p className="mt-7 text-base sm:text-lg text-[var(--ci-grey)] leading-relaxed">
            {t("hygieneSection.copy")}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((item, i) => {
            const Icon = ICONS[i] || Sparkles;
            return (
              <motion.article
                key={item.title}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.65, delay: i * 0.12 }}
                className="group bg-[#F7F9FA] border border-[#E3E8EC] rounded-2xl p-10 lg:p-12 hover:-translate-y-1 hover:border-[var(--ci-turquoise)]/40 hover:shadow-[0_20px_60px_rgba(0,91,234,0.08)] transition-all duration-300 flex flex-col h-full"
                data-testid={`hygiene-card-${i + 1}`}
              >
                <span className="w-14 h-14 rounded-full bg-[var(--ci-blue-light)] text-[var(--ci-blue)] flex items-center justify-center group-hover:bg-[var(--ci-blue)] group-hover:text-white transition-colors duration-300 shrink-0" data-testid={`hygiene-card-icon-${i + 1}`}>
                  <Icon size={24} />
                </span>
                <h3 className="text-xl sm:text-2xl font-semibold tracking-tight text-[var(--ci-grey)] mt-8">{item.title}</h3>
                <p className="text-sm text-[var(--ci-grey)] leading-relaxed mt-4 flex-1">{item.desc}</p>
                <ul className="mt-auto pt-8 space-y-2.5">
                  {item.tags.map((tag) => (
                    <li key={tag} className="flex items-center gap-3 font-tech text-[11px] tracking-[0.14em] uppercase text-[var(--ci-grey)]">
                      <HammLogoA data-testid="bullet-logo-a" />
                      {tag}
                    </li>
                  ))}
                </ul>
              </motion.article>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-16"
        >
          <Link to={locale === "en" ? "/en/inquiry-portal" : "/anfrage-portal"} data-testid="hygiene-cta-btn"
            className="flex items-center gap-3 px-8 h-14 rounded-full border-2 border-[var(--ci-grey)] text-[var(--ci-grey)] hover:bg-[var(--ci-grey)] hover:text-white font-tech text-xs tracking-[0.2em] uppercase transition-colors duration-200 active:scale-[0.98]">
            {t("hygieneSection.cta")} <ArrowUpRight size={16} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
